import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseNavigationService, ReturnUrlService } from '@lx/quote-tool/lite/shared/services';
import { SessionStorageService } from '@lx/shared/storage';
import { Observable, Subject } from 'rxjs';
import { QuoteToolLiteStore } from '../store';
import { RouteIds, Step, STORE_RESET_KEY } from '../types';

/**
 * Quote Tool Lite navigation service that composes the BaseNavigationService
 * and adds application-specific functionality.
 *
 * This service uses composition over inheritance to:
 * - Maintain loose coupling with the base navigation service
 * - Sync navigation events with the QuoteToolLiteStore
 * - Provide app-specific features like closing the quote tool
 *
 * Architecture:
 * BaseNavigationService (emits events) → This service (orchestrates) → Store (updates state)
 *
 * @example
 * ```typescript
 * constructor(private navService: NavigationService) {
 *   this.navService.navigateToNextStep(currentStep);
 *   this.navService.currentStep$.subscribe(step => console.log(step));
 * }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class NavigationService {
  /**
   * Observable that emits the current step whenever navigation occurs.
   * Delegated from the base navigation service.
   */
  readonly currentStep$: Observable<Step>;

  /**
   * Observable that emits when the quote tool should be closed
   */
  readonly closeQuoteToolLite$: Observable<void>;

  private readonly _closeQuoteToolLite$ = new Subject<void>();
  private readonly baseNavigationService = inject(BaseNavigationService<RouteIds>);
  private readonly store = inject(QuoteToolLiteStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly returnUrlService = inject(ReturnUrlService);
  private readonly sessionStorageService = inject(SessionStorageService);

  constructor() {
    this.subscribeToNavigationEvents();
  }

  /**
   * Navigates to the next step based on the current step's nextStep callback
   * @param step - The current step containing the nextStep callback
   */
  navigateToNextStep(step: Step): void {
    this.baseNavigationService.navigateToNextStep(step);
  }

  /**
   * Navigates to a specific step by route ID
   * @param step - The route ID to navigate to
   * @param callback - Optional callback to execute after navigation completes
   */
  navigateToStep(step: RouteIds, callback?: () => void): void {
    this.baseNavigationService.navigateToStep(step, callback);
  }

  /**
   * Navigates to the first step in the quote tool flow
   * @param callback - Optional callback to execute after navigation completes
   */
  navigateToFirstStep(callback?: () => void): void {
    this.baseNavigationService.navigateToFirstStep(callback);
  }

  /**
   * Closes the Quote Tool Lite application and navigates back to the return URL.
   * Sets a cleanup flag in session storage to ensure the store is reset when the user
   * returns to the application. This allows users to refresh the confirmation page
   * without losing their booking details, while ensuring a fresh start when they
   * explicitly navigate away.
   *
   * If no return URL is available, the store is reset immediately.
   */
  closeQuoteToolLite() {
    if (this.returnUrlService.hasReturnUrl()) {
      // Set flag to reset store on next app initialization
      this.sessionStorageService.set(STORE_RESET_KEY, true);
      // Navigate to return URL
      this.returnUrlService.navigateToReturnUrl();
    } else {
      // If there is no return URL, reset the store immediately and navigate to the first step
      this.navigateToFirstStep(() => this.store.resetStore());
    }
  }

  /**
   * Subscribes to navigation events from the base service and updates the store.
   * This creates a unidirectional data flow: Navigation → Store (no circular dependency)
   * @private
   */
  private subscribeToNavigationEvents(): void {
    this.baseNavigationService.currentStep$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((step: Step) => {
      this.store.updateCurrentStep(step);
    });
  }
}
